/**
 * Employee skills — the structured list stored in the single `skills` column.
 *
 * The column used to hold a comma-separated string of names, and every row
 * written before this still does, so everything here reads both shapes: a JSON
 * array is the current form, anything else is parsed as the legacy list. An
 * old record therefore opens — and re-saves — without losing its names.
 *
 * The legacy `program_name`, `comp_date` and `certificate` columns described
 * ONE certification for the whole employee. They are folded into the first
 * entry on read and mirrored back from it on write (see legacyCertification),
 * so the Documents list and anything else reading those columns keeps working.
 */

export const PROFICIENCY_LEVELS = [
  "Beginner",
  "Intermediate",
  "Advanced",
  "Expert",
];

export const DEFAULT_PROFICIENCY = "Intermediate";

// Certificates are one-per-skill, so the cap is deliberately lower than the
// 10 MB the KYC documents allow — this is a scan of a course certificate.
export const SKILL_CERT_MAX_BYTES = 5 * 1024 * 1024;

// Narrower than the server's allow-list (which also takes .doc/.docx) because
// the field asks for a certificate, and a file the server silently drops is
// worse than one the picker never offered.
export const SKILL_CERT_ACCEPT = ".pdf,.png,.jpg,.jpeg";
const CERT_EXT_RE = /\.(pdf|png|jpe?g)$/i;

// Rows carry a stable id so a React key survives a removal in the middle of
// the list — without it, deleting the second card re-mounts every card below.
let rowSeq = 0;
const nextRowId = () => `skill-${(rowSeq += 1)}`;

const str = (value) => (value == null ? "" : String(value).trim());

// Dates arrive as 'YYYY-MM-DD' from the API but as a full ISO timestamp from a
// raw row; <input type="date"> only accepts the former.
const dateOnly = (value) => str(value).split("T")[0];

const levelOf = (value) => {
  const wanted = str(value).toLowerCase();
  return (
    PROFICIENCY_LEVELS.find((l) => l.toLowerCase() === wanted) ||
    DEFAULT_PROFICIENCY
  );
};

/** CSS modifier for the proficiency badge: `is-advanced`, `is-expert`, … */
export const levelTone = (value) => levelOf(value).toLowerCase();

/**
 * One row of the editor. `file` is a File picked but not yet uploaded and
 * `savedIndex` is the row's position in the JSON as it is stored right now —
 * the certificate stream endpoint addresses documents by that index, and it
 * must not shift when a row above is removed before the form is saved.
 */
export const makeSkillRow = (patch = {}) => ({
  id: nextRowId(),
  name: "",
  level: DEFAULT_PROFICIENCY,
  program: "",
  completed_on: "",
  certificate: "",
  file: null,
  savedIndex: null,
  ...patch,
});

const dedupe = (rows) => {
  const seen = new Set();
  return rows.filter((row) => {
    const key = row.name.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/**
 * Parse the stored value into editor rows.
 *
 * @param {string|Array} raw     the `skills` column
 * @param {object} legacy        { program_name, comp_date, certificate }
 * @returns {Array} rows, de-duplicated by name, each tagged with its savedIndex
 */
export function parseSkills(raw, legacy = {}) {
  let parsed = null;
  if (Array.isArray(raw)) {
    parsed = raw;
  } else {
    const text = str(raw);
    if (text.startsWith("[")) {
      try {
        const json = JSON.parse(text);
        if (Array.isArray(json)) parsed = json;
      } catch {
        parsed = null; // not JSON after all — fall through to the legacy split
      }
    }
  }

  let rows = [];
  if (parsed) {
    parsed.forEach((entry) => {
      if (!entry) return;
      if (typeof entry === "string") {
        const name = str(entry);
        if (name) rows.push(makeSkillRow({ name }));
        return;
      }
      const name = str(entry.name ?? entry.skill);
      if (!name) return;
      rows.push(
        makeSkillRow({
          name,
          level: levelOf(entry.level ?? entry.proficiency),
          program: str(entry.program ?? entry.program_name),
          completed_on: dateOnly(entry.completed_on ?? entry.comp_date),
          certificate: str(entry.certificate),
        }),
      );
    });
  } else {
    str(raw)
      .split(/[,;\n]/)
      .map(str)
      .filter(Boolean)
      .forEach((name) => rows.push(makeSkillRow({ name })));
  }

  rows = dedupe(rows);

  // The one-per-employee certification only belongs to the first entry, and
  // only while that entry has none of its own — on a JSON row these columns
  // are just a mirror of what is already there.
  const program = str(legacy.program_name);
  const completed = dateOnly(legacy.comp_date);
  const certificate = str(legacy.certificate);
  if (
    rows.length &&
    (program || completed || certificate) &&
    !rows[0].program &&
    !rows[0].completed_on &&
    !rows[0].certificate
  ) {
    rows[0] = { ...rows[0], program, completed_on: completed, certificate };
  }

  return rows.map((row, index) => ({ ...row, savedIndex: index }));
}

/** Rows → the string written to the `skills` column ("" when there are none). */
export function serializeSkills(rows = []) {
  const seen = new Set();
  const out = [];
  rows.forEach((row) => {
    const name = str(row?.name);
    if (!name) return;
    const key = name.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push({
      name,
      level: levelOf(row.level),
      program: str(row.program),
      completed_on: dateOnly(row.completed_on),
      certificate: str(row.certificate),
    });
  });
  return out.length ? JSON.stringify(out) : "";
}

/** Just the names — for the tag clouds on the profile pages. */
export function skillNames(raw) {
  return parseSkills(raw).map((row) => row.name);
}

/**
 * Apply a plain comma-separated list of names over the stored value, keeping
 * the proficiency, program and certificate of every name that survives. This
 * is what Settings › My Profile edits: a text field there must not throw away
 * the structured detail entered in the team wizard.
 */
export function mergeSkillNames(raw, names = []) {
  const existing = new Map(
    parseSkills(raw).map((row) => [row.name.toLowerCase(), row]),
  );
  const rows = [];
  String(names)
    .split(/[,;\n]/)
    .map(str)
    .filter(Boolean)
    .forEach((name) => {
      const prev = existing.get(name.toLowerCase());
      rows.push(prev ? { ...prev, name } : makeSkillRow({ name }));
    });
  return serializeSkills(rows);
}

/**
 * The legacy single-certification columns, taken from the first row that has
 * one. Sent alongside the JSON so nothing reading those columns goes blank.
 */
export function legacyCertification(rows = []) {
  const first =
    rows.find((row) => row.program || row.completed_on || row.certificate) ||
    null;
  return {
    program_name: first ? str(first.program) : "",
    comp_date: first ? dateOnly(first.completed_on) : "",
    certificate: first ? str(first.certificate) : "",
  };
}

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/** '2026-09-15' → '15 Sep 2026'. Built from the parts: `new Date()` on a
 *  date-only string parses as UTC and prints the day before east of it. */
export function formatSkillDate(value) {
  const date = dateOnly(value);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return "";
  const [year, month, day] = date.split("-").map(Number);
  if (!MONTHS[month - 1]) return "";
  return `${String(day).padStart(2, "0")} ${MONTHS[month - 1]} ${year}`;
}

/** Display name for a stored path or a pending File. */
export function certificateName(value) {
  if (!value) return "";
  if (typeof value !== "string") return value.name || "certificate";
  return value.split("/").pop();
}

/** Reject a certificate the server would drop or refuse. Returns an error
 *  message, or "" when the file is fine. */
export function validateCertificate(file) {
  if (!file) return "";
  if (!CERT_EXT_RE.test(file.name || "")) {
    return "Certificate must be a PDF or an image (PNG/JPG).";
  }
  if (file.size > SKILL_CERT_MAX_BYTES) {
    return "Certificate must be 5 MB or smaller.";
  }
  return "";
}
